import { useForm } from "@inertiajs/react";
import { BookOpen, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import FormField from "@/components/forms/FormField";
import MinutesField from "@/components/forms/MinutesField";
import { cn } from "@/lib/utils";
import { formatMinutesDuration, isValidMinuteTotal } from "@/lib/minutes";
import { pluralize } from "@/lib/format";
import { useSubmitLock } from "@/lib/useSubmitLock";

// The reading blocks most classes log.
const QUICK_MINUTES = [15, 20, 30, 45];

/**
 * The bulk minute-logging card from the teacher's "Log minutes" page.
 *
 * Minutes first (with one-tap common values), then the roster with
 * everyone pre-checked, so the usual case — "the whole class read for
 * 20 minutes" — is one tap and submit. Uncheck anyone who was away.
 * Tapping anywhere on a student's row toggles them.
 *
 * Also rendered by the help centre as a "screenshot".
 */
export default function LogMinutesCard({
  students,
  submit_path = "#",
  initialMinutes = "",
  initialNote = "",
  initialAllChecked = true,
}) {
  const initialIds = initialAllChecked ? students.map((s) => s.id) : students.filter((s) => s.checked).map((s) => s.id);
  const { data, setData, post, processing, errors } = useForm({
    student_ids: initialIds,
    minutes: initialMinutes,
    note: initialNote,
  });
  const { submitting, startSubmit, finishSubmit } = useSubmitLock(processing);

  const allSelected = data.student_ids.length === students.length;
  const noneSelected = data.student_ids.length === 0;
  const minutesValid = isValidMinuteTotal(data.minutes);

  const toggleStudent = (id) => {
    setData(
      "student_ids",
      data.student_ids.includes(id) ? data.student_ids.filter((x) => x !== id) : [...data.student_ids, id]
    );
  };

  const submit = (e) => {
    e.preventDefault();
    if (noneSelected || !minutesValid) return;
    if (!startSubmit()) return;

    post(submit_path, { onFinish: finishSubmit });
  };

  const submitLabel = submitting
    ? "Logging…"
    : !minutesValid
    ? "Enter minutes to log"
    : noneSelected
    ? "Choose at least one student"
    : `Log ${formatMinutesDuration(data.minutes)} for ${pluralize(data.student_ids.length, "student")}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          How long did they read?
        </CardTitle>
        <CardDescription>Everyone checked gets the same minutes, in one log you can fix or undo later.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-6">
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 sm:flex sm:flex-wrap" role="group" aria-label="Common reading times">
              {QUICK_MINUTES.map((minutes) => {
                const selected = String(data.minutes) === String(minutes);
                return (
                  <button
                    key={minutes}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setData("minutes", String(minutes))}
                    className={cn(
                      "min-h-11 whitespace-nowrap rounded-full border px-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-4",
                      selected ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-foreground hover:bg-secondary/60"
                    )}
                  >
                    {minutes} min
                  </button>
                );
              })}
            </div>
            {/* Two short numbers — keep the inputs compact on wide screens. */}
            <div className="sm:max-w-sm">
              <MinutesField
                name="minutes"
                required
                placeholder="20"
                value={data.minutes}
                onChange={(e) => setData("minutes", e.target.value)}
                error={errors.minutes}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p id="students-label" className="text-sm font-medium text-foreground">
                Who read? <span className="font-normal text-muted-foreground">({data.student_ids.length} of {students.length})</span>
              </p>
              <div className="flex items-center gap-1">
                <Button type="button" variant="ghost" size="sm" onClick={() => setData("student_ids", students.map((s) => s.id))} disabled={allSelected}>
                  <CheckSquare className="h-4 w-4" /> All
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setData("student_ids", [])} disabled={noneSelected}>
                  <Square className="h-4 w-4" /> None
                </Button>
              </div>
            </div>
            {/* Wide screens fit more of the class at a glance. */}
            <div role="group" aria-labelledby="students-label" className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {students.length === 0 ? (
                <p className="col-span-full rounded-lg border border-border px-4 py-8 text-center text-sm text-muted-foreground">
                  No students in this class yet. Your school admin adds the class list.
                </p>
              ) : (
                students.map((student) => {
                  const checked = data.student_ids.includes(student.id);
                  return (
                    <label
                      key={student.id}
                      className={cn(
                        "flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-4 py-2.5 transition-colors",
                        checked ? "border-primary/40 bg-primary/5 hover:bg-primary/10" : "border-border bg-card hover:bg-secondary/60"
                      )}
                    >
                      <Checkbox checked={checked} onChange={() => toggleStudent(student.id)} />
                      <span className={cn("flex-1 text-sm", checked ? "font-medium text-foreground" : "text-muted-foreground")}>
                        {student.full_name}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <FormField
            name="note"
            label="Note (optional)"
            placeholder="Library time · fiction"
            value={data.note}
            onChange={(e) => setData("note", e.target.value)}
            error={errors.note}
            help="Shows with the log in your class's activity and on each family's page."
          />

          {/* Pinned to the bottom of the screen on phones, so it's in reach after scrolling the roster. */}
          <div className="sticky bottom-0 -mx-6 border-t border-border bg-card/95 px-6 py-3 backdrop-blur sm:static sm:mx-0 sm:bg-transparent sm:px-0 sm:pb-0 sm:pt-4 sm:backdrop-blur-none">
            <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={submitting || noneSelected || !minutesValid}>
              {submitLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
